/**
 * Handles all the calculations for event items.
 * Used inside of the EventTotals service.
 */
export function EventItemTotalsService ( bigDecimalApi, sessionObjAPI ) {
  var EventItemTotals = function (event) {
    this.event = event;
    this.info = sessionObjAPI.currency();
    this.company = sessionObjAPI.company();
  };

  var calculateMultiTax = function (taxRates, total, item, hasXero) {
    var rateSummation = BigDecimal.ZERO;
    var tax           = BigDecimal.ZERO;

    // Rounding is handled a bit differently for Xero
    // users than it is for others. We add the tax rates
    // together, multiply the subtotal of the current
    // item by the summation of the tax rates, then round.
    if (hasXero) {

      angular.forEach(taxRates, function (rate) {
        rateSummation = rateSummation.add(rate.labor_gst_free && item.product_type === 'J' ? BigDecimal.ZERO: rate.tax_rate);
      });

      tax = total.multiply(rateSummation.movePointLeft(2)).setScale(2, BigDecimal.ROUND_HALF_UP);
    } else {

      // If the tax rate has labor_gst_free, the result is zero,
      // otherwise calculate like normal.
      angular.forEach(taxRates, function (rate) {
        rate.multi_tax_item_result = (item.product_type === 'J' && rate.labor_gst_free) ?
          BigDecimal.ZERO:
          total.multiply(rate.tax_rate.movePointLeft(2)).setScale(2, BigDecimal.ROUND_HALF_UP);
      });

      angular.forEach(taxRates, function (rate) {
        tax = tax.add(rate.multi_tax_item_result);
      });
    }

    return tax.toString();
  };

  var calculateMultiTaxTotal = function (taxRates, total, item) {
    // Calculate each tax group item separately,
    // then add each to the total.
    angular.forEach(taxRates, function (rate) {
      rate.multi_tax_total_item_result = (item.product_type === 'J' && rate.labor_gst_free) ?
        BigDecimal.ZERO:
        total.multiply(rate.tax_rate.movePointLeft(2)).setScale(2, BigDecimal.ROUND_HALF_UP);
    });

    angular.forEach(taxRates, function (rate) {
      total = total.add(rate.multi_tax_total_item_result);
    });

    return total.toString();
  };

  var calculateSalesTax = function (taxRate, total) {
    return total.multiply(taxRate.movePointLeft(2))
      .setScale(2, BigDecimal.prototype.ROUND_HALF_UP).toString();
  };

  var calculateSalesTaxTotal = function (taxRate, total) {

    // Calculate tax like
    // normal using the predefined sales taxRate.
    return total
        .multiply(taxRate.movePointLeft(2))
          .setScale(2, BigDecimal.prototype.ROUND_HALF_UP)
            .add(total)
              .setScale(2, BigDecimal.prototype.ROUND_HALF_UP).toString();
  };

  var extractSalesTax = function (taxRate, total) {
    const subtotal = total.subtract(
      total.setScale(4, BigDecimal.ROUND_HALF_UP)
      .divide(
        BigDecimal.ONE.add(taxRate.movePointLeft(2))
        .setScale(4, BigDecimal.ROUND_HALF_UP)
      )
    );

    return subtotal.toString();
  };

  var getTaxRates = function (taxGroup) {
    var taxRates = taxGroup.tax_group_items_attributes;

    angular.forEach(taxRates, function (rate) {
      rate.tax_rate = new BigDecimal(rate.tax_rate ? rate.tax_rate.toString(): '0').setScale(2, BigDecimal.ROUND_HALF_UP);
    });

    return taxRates;
  };

  /**
   * Determines whether or not the event is closed based on status
   * @return {boolean} true if closed, false if open
   */
  EventItemTotals.prototype.closed = function () {
    var self = this;

    return self.event.event_status === 'C';
  };

  /**
   * Calculates the total of the event item
   * @param  {object} item The event item to calculate the total
   * @return {string}      The total of the event item
   */
  EventItemTotals.prototype.total = function (item) {
    const self = this;

    const quantity  = bigDecimalApi.get(item.quantity, 2);
    const taxRate   = bigDecimalApi.get(item.sales_tax_rate, 2);
    const unitPrice = bigDecimalApi.get(item.unit_price, 2);

    const total = unitPrice.multiply(quantity).setScale(2, BigDecimal.ROUND_HALF_UP);

    let taxRates;

    if (self.company.use_multi_tax) {
      taxRates = getTaxRates(self.event.tax_group);
    }

    // If event is closed, just return the server value
    if (self.closed()) {
      return item.amount || BigDecimal.ZERO.toString();
    }

    // If the product is tax free or the price includes tax or the customer is a tax free customer, return the total
    if (item.gst_free || self.info.price_includes_tax || self.event.tax_free) {
      return total.toString();
    }

    // If not, add in the tax to the total
    else {
      return self.company.use_multi_tax ? calculateMultiTaxTotal(taxRates, total, item): calculateSalesTaxTotal(taxRate, total);
    }
  };

  /**
   * Calculates the tax of the event item
   * @param  {object} item The event item to calculate the tax
   * @return {string}      The total tax of the event item
   */
  EventItemTotals.prototype.tax = function(item) {
    const self = this;

    let tax = bigDecimalApi.get();

    const quantity  = bigDecimalApi.get(item.quantity, 2);
    const taxRate   = bigDecimalApi.get(item.sales_tax_rate, 2);
    const unitPrice = bigDecimalApi.get(item.unit_price, 2);

    const total = unitPrice.multiply(quantity).setScale(2, BigDecimal.ROUND_HALF_UP);

    let taxRates;

    if (self.company.use_multi_tax) {
      taxRates = getTaxRates(self.event.tax_group);
    }

    // If event is closed, just return the server value
    if (self.closed()) {
      return item.gst || BigDecimal.ZERO.toString();
    }

    // If product is tax free then obviously return 0 for tax
    else if (item.gst_free || self.event.tax_free) {
      return BigDecimal.ZERO.toString();
    }

    // If the product includes tax, extract out the tax from the total
    else if (self.info.price_includes_tax) {
      tax = extractSalesTax(taxRate, total);
    }

    // If not then just calculate it based on the tax rate
    else {
      tax = self.company.use_multi_tax ? calculateMultiTax(taxRates, total, item, self.company.has_xero): calculateSalesTax(taxRate, total);
    }

    return tax;
  };

  return EventItemTotals;
}